import React from 'react';
import { Bell, CheckCircle2, Clock, MessageSquare, ShieldCheck, ArrowRight, ArrowLeft } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Notification } from '../../types';
import { PushNotificationSettingsCard } from '../../components/notifications/PushNotificationSettingsCard';

export const NotificationsPage: React.FC = () => {
  const { currentUser, notifications, markAllNotificationsAsRead, markNotificationAsRead, navigateTo } = useApp();

  const handleNotificationClick = (notif: Notification) => {
    markNotificationAsRead(notif.id);
    if (notif.targetView) {
      navigateTo(notif.targetView as any, { jobId: notif.targetJobId });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigateTo('home')}
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1.5 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </button>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-emerald-600" />
            <span>Marketplace Notifications</span>
          </h1>
        </div>

        {notifications.some((n) => !n.read) && (
          <button
            onClick={markAllNotificationsAsRead}
            className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200"
          >
            Mark All as Read
          </button>
        )}
      </div>

      {currentUser?.id && (
        <PushNotificationSettingsCard
          userId={currentUser.id}
          userRole={currentUser.role === 'professional' ? 'professional' : 'customer'}
        />
      )}

      <div className="bg-white rounded-3xl border border-slate-200 shadow-xs divide-y divide-slate-100 overflow-hidden">
        {notifications.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-xs space-y-2">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2">
              <Bell className="w-6 h-6" />
            </div>
            <p className="font-bold text-slate-800 text-sm">No notifications yet</p>
            <p className="text-slate-400 max-w-xs mx-auto">
              You will be notified here when professionals submit quotes, messages arrive, or job updates occur.
            </p>
          </div>
        ) : (
          notifications.map((notif) => (
            <div
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              className={`p-4 sm:p-5 transition-colors cursor-pointer flex items-start justify-between gap-4 ${
                notif.read ? 'bg-white hover:bg-slate-50/80' : 'bg-emerald-50/40 hover:bg-emerald-50/70'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    notif.type === 'quote_received'
                      ? 'bg-amber-100 text-amber-800'
                      : notif.type === 'quote_accepted'
                      ? 'bg-emerald-100 text-emerald-800'
                      : notif.type === 'verification'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {notif.type === 'quote_received' ? (
                    <Clock className="w-4 h-4" />
                  ) : notif.type === 'quote_accepted' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : notif.type === 'verification' ? (
                    <ShieldCheck className="w-4 h-4" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                </div>

                <div>
                  <h4 className="font-bold text-xs sm:text-sm text-slate-900">{notif.title}</h4>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{notif.message}</p>
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    {new Date(notif.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {!notif.read && (
                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                )}
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
